import { SomeoneTypingEvent, TYPING_FORGOTTEN_AFTER_MS, TYPING_SAID_EVERY_MS } from '@smatchy/chat-domain';

/**
 * « Karim écrit… », as Maude validated it on 31/10/2026 (« Ton mélange »):
 * the name under the conversation's title, and three dots where the next
 * message will appear.
 *
 * Nothing says « stopped ». Somebody is shown typing until
 * TYPING_FORGOTTEN_AFTER_MS after their last signal, or until their message
 * arrives, whichever comes first: a phone that loses its network stops
 * « typing » by itself instead of leaving dots up forever.
 */

/** Somebody typing, and until when the screen shows it. */
export interface SomeoneTypingNow {
  userId: string;
  name: string;
  until: number;
}

/**
 * After a signal: that person is typing for another while. Mine are left out:
 * the API never sends my own back, but my other phone may be in the room.
 */
export function afterATypingSignal(
  typing: SomeoneTypingNow[],
  signal: SomeoneTypingEvent,
  now: number,
  myUserId: string | undefined,
): SomeoneTypingNow[] {
  if (!signal?.userId || signal.userId === myUserId) {
    return typing;
  }
  const others = typing.filter((one) => one.userId !== signal.userId);
  return [...others, { userId: signal.userId, name: signal.name, until: now + TYPING_FORGOTTEN_AFTER_MS }];
}

/** Their message arrived: the dots give way to it at once. */
export function afterTheirMessage(typing: SomeoneTypingNow[], sentBy: string): SomeoneTypingNow[] {
  return typing.some((one) => one.userId === sentBy) ? typing.filter((one) => one.userId !== sentBy) : typing;
}

/** Who is still typing now; the same list when nobody stopped, so nothing redraws. */
export function stillTyping(typing: SomeoneTypingNow[], now: number): SomeoneTypingNow[] {
  return typing.every((one) => one.until > now) ? typing : typing.filter((one) => one.until > now);
}

/** What the header says under the title, with the key that says it. */
export type WhatTheHeaderSays =
  | { key: 'MESSAGE_PAGE.DISCUSSION.TYPING_ONE'; params: { name: string } }
  | { key: 'MESSAGE_PAGE.DISCUSSION.TYPING_TWO'; params: { first: string; second: string } }
  | { key: 'MESSAGE_PAGE.DISCUSSION.TYPING_MANY'; params: { count: number } };

/** One name, two names, then a count: three names do not fit under a title. */
export function whatTheHeaderSays(typing: SomeoneTypingNow[]): WhatTheHeaderSays | null {
  if (typing.length === 0) {
    return null;
  }
  if (typing.length === 1) {
    return { key: 'MESSAGE_PAGE.DISCUSSION.TYPING_ONE', params: { name: typing[0].name } };
  }
  if (typing.length === 2) {
    return { key: 'MESSAGE_PAGE.DISCUSSION.TYPING_TWO', params: { first: typing[0].name, second: typing[1].name } };
  }
  return { key: 'MESSAGE_PAGE.DISCUSSION.TYPING_MANY', params: { count: typing.length } };
}

/**
 * Whether the app says « I am typing » now: words in the box, and nothing
 * said for TYPING_SAID_EVERY_MS. An emptied box says nothing, and the dots
 * go away on the other side by themselves.
 */
export function iSayIAmTyping(text: string | null | undefined, lastSaidAt: number | undefined, now: number): boolean {
  if (!text?.trim()) {
    return false;
  }
  return lastSaidAt === undefined || now - lastSaidAt >= TYPING_SAID_EVERY_MS;
}
